import { fmtTRYParts } from "@/lib/format";

export const PageHeader = ({ title, subtitle, action, testId }) => (
  <div
    className="flex items-start justify-between gap-4"
    data-testid={testId}
    data-slot="page-header"
  >
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// Sticky wrapper for the top area of every main page: title, subtitle, primary
// action button, and (optionally) the search / filter row.  Sits inside the
// main scroll container.  On mobile the app has a ~60px sticky topbar so the
// header sticks at ``top-[60px]``; on desktop there is no topbar so ``top-0``.
// Uses backdrop-blur so table rows sliding underneath stay legible and a
// negative horizontal margin so the strip reaches the full main padding.
export const StickyPageHead = ({ children, className = "" }) => (
  <div
    className={
      "sticky top-[60px] md:top-0 z-20 " +
      "-mx-4 md:-mx-8 -mt-4 md:-mt-8 " +
      "px-4 md:px-8 py-4 md:py-6 " +
      "bg-slate-50/85 supports-[backdrop-filter]:bg-slate-50/70 backdrop-blur " +
      "border-b border-slate-200 " +
      "space-y-3 " + className
    }
  >
    {children}
  </div>
);

export const StatCard = ({ label, value, amount, hint, accent = "slate", testId }) => {
  const accentClass = {
    slate: "text-slate-900",
    emerald: "text-emerald-600",
    red: "text-red-600",
    amber: "text-amber-600",
    blue: "text-blue-600",
  }[accent] || "text-slate-900";

  // Dashboard KPI kartlarında "kuruş" (,99) daha küçük ve hafif render edilir
  // → premium finansal görünüm.  Mevcut ``value`` prop'u (string) API'si
  // korunur; ``amount`` prop'u sadece opt-in olarak Dashboard'da kullanılır.
  let display;
  if (amount !== undefined && amount !== null) {
    const { integer, decimal } = fmtTRYParts(amount);
    display = (
      <span className="tabular-nums whitespace-nowrap">
        {integer}
        {decimal && (
          <span className="text-[0.6em] font-semibold opacity-70 align-baseline ml-0.5">
            {decimal}
          </span>
        )}
      </span>
    );
  } else {
    display = value;
  }

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors"
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className={`mt-2 font-display text-2xl md:text-[1.75rem] font-bold tracking-tight leading-tight ${accentClass}`}>
        {display}
      </div>
      {hint && <div className="text-xs text-slate-500 mt-1.5">{hint}</div>}
    </div>
  );
};

export const SectionCard = ({ title, action, children, testId, className = "" }) => (
  <div
    className={`bg-white border border-slate-200 rounded-xl ${className}`}
    data-testid={testId}
  >
    {(title || action) && (
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <h2 className="font-display text-base font-semibold tracking-tight text-slate-900">{title}</h2>
        {action}
      </div>
    )}
    <div>{children}</div>
  </div>
);

export const EmptyState = ({ title = "Henüz kayıt yok", hint, action }) => (
  <div className="text-center py-12 px-6">
    <div className="font-display text-base font-medium text-slate-700">{title}</div>
    {hint && <div className="text-sm text-slate-500 mt-1">{hint}</div>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  invoiced: "bg-blue-50 text-blue-700 border-blue-200",
  non_invoiced: "bg-slate-100 text-slate-600 border-slate-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  passive: "bg-slate-100 text-slate-600 border-slate-200",
  company: "bg-blue-50 text-blue-700 border-blue-200",
  personal: "bg-violet-50 text-violet-700 border-violet-200",
};

export const StatusBadge = ({ value, label, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
      STATUS_STYLES[value] || "bg-slate-100 text-slate-700 border-slate-200"
    }`}
  >
    {label || value}
  </span>
);
